import * as React from "react";
import { type DateRange } from "react-day-picker";
import { subDays, differenceInDays, addDays, format } from "date-fns";
import { FilterContainer } from "@/components/FiltersV2";
import { CustomDatePicker } from "@/components/CustomDatePicker";
import { ModelsSelector } from "@/components/ModelsSelector";
import { GranularitySelector, type GranularityValue } from "@/components/GranularitySelector";
import { GroupBySelector, type GroupByValue } from "@/components/GroupBySelector";
import { FiltersSelector } from "@/components/FiltersSelector";
import { LastXDaysSpend } from "@/components/Dashboard/LastXDaysSpend";
import { DashboardCard } from "@/components/Dashboard";
import { BiDollarCircle } from "react-icons/bi";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { IconDownload } from "@tabler/icons-react";
import { useBreakdownTable, useBreakdownTimeSeries } from "@/data/hooks/api/useBreakdown";
import { useSavedViews, relativeDateToRange } from "@/contexts/SavedViewsContext";
import { useCreateReport } from "@/data/hooks/api";
import { useAuth } from "@/hooks/useAuth";
import { UpgradeBanner } from "@/components/UpgradeBanner";
import { BreakdownDetailSidebar } from "@/components/Breakdown/BreakdownDetailSidebar";
import type { BreakdownTableItem, BreakdownTableRequest, BreakdownTimeSeriesRequest } from "@/data/types/breakdown.types";
import type { SortingState } from "@tanstack/react-table";
import { toast } from "sonner";
import { columns } from "./logic";
import type { TableRowData } from "@/data/types/breakdown.types";
import type { CreateReportRequest, ReportPayload, MetadataFilter } from "@/pages/Reports/types";
import type { ModelSelection } from "@/data/types/analytics.types";

// Default date range (last 30 days)
const DEFAULT_DATE_RANGE: DateRange = {
  from: subDays(new Date(), 29),
  to: new Date(),
};

// Default selections for models
const ALL_MODELS_SELECTION: ModelSelection = { all: true, providers: [], models: [] };

export function BreakdownPage() {
  const { currentFilters, setCurrentFilters, setAllFiltersState, appliedView, clearAppliedView } = useSavedViews();

  // Filter state
  const [workingDateRange, setWorkingDateRange] = React.useState<DateRange | undefined>(DEFAULT_DATE_RANGE);
  const [appliedDateRange, setAppliedDateRange] = React.useState<DateRange | undefined>(DEFAULT_DATE_RANGE);
  const [modelSelection, setModelSelection] = React.useState<ModelSelection>(ALL_MODELS_SELECTION);
  const [granularity, setGranularity] = React.useState<GranularityValue>('daily');
  const [groupBy, setGroupBy] = React.useState<GroupByValue>('modality');

  // Table pagination state
  const [pageIndex, setPageIndex] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'totalCosts', desc: true },
  ]);

  // API hooks for report creation
  const createReportMutation = useCreateReport();
  const { user, isOnFreePlan } = useAuth();

  // Sidebar state
  const [selectedRow, setSelectedRow] = React.useState<TableRowData | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

  const handleRowClick = React.useCallback((row: TableRowData) => {
    // Don't open sidebar for the total row
    if (row.isTotal) return;
    setSelectedRow(row);
    setIsSidebarOpen(true);
  }, []);

  const handleSidebarClose = React.useCallback((open: boolean) => {
    setIsSidebarOpen(open);
    if (!open) {
      setSelectedRow(null);
    }
  }, []);

  // Handle when a saved view is applied - update all filter states
  React.useEffect(() => {
    if (!appliedView) return;

    // Update date range from relative date or use default
    const dateRange = relativeDateToRange(appliedView.relativeDate) || DEFAULT_DATE_RANGE;
    setWorkingDateRange(dateRange);
    setAppliedDateRange(dateRange);

    // Update models if specified in the view
    if (appliedView.modelSelection) {
      setModelSelection(appliedView.modelSelection);
    } else if (appliedView.models && appliedView.models.length > 0) {
      // Legacy support for old views with models array
      setModelSelection({ all: false, providers: [], models: appliedView.models });
    }

    // Update granularity if specified
    if (appliedView.granularity) {
      setGranularity(appliedView.granularity as GranularityValue);
    }

    // Update groupBy if specified
    if (appliedView.groupBy) {
      setGroupBy(appliedView.groupBy as GroupByValue);
    }

    // Reset pagination
    setPageIndex(0);

    // Clear the applied view after processing to prevent re-triggering
    clearAppliedView();

    toast.success(`Applied view: ${appliedView.title}`);
  }, [appliedView, clearAppliedView]);

  // Sync all filter state with SavedViewsContext for saving views
  React.useEffect(() => {
    setAllFiltersState({
      dateRange: appliedDateRange,
      granularity,
      dimension: groupBy,
      modelSelection,
      providers: undefined,
      metadataFilters: currentFilters,
    });
  }, [appliedDateRange, granularity, groupBy, modelSelection, currentFilters, setAllFiltersState]);

  const handleDateRangeApply = React.useCallback((range: DateRange | undefined) => {
    if (!range?.from || !range?.to) {
      setAppliedDateRange(range);
      return;
    }

    const days = differenceInDays(range.to, range.from) + 1;
    let finalRange = range;

    if (granularity === 'daily' && days > 90) {
      finalRange = {
        from: range.from,
        to: addDays(range.from, 89)
      };
      toast.info('Range adjusted to 90 days for daily granularity');
    } else if (granularity === 'hourly' && days > 14) {
      finalRange = {
        from: range.from,
        to: addDays(range.from, 13)
      };
      toast.info('Range adjusted to 14 days for hourly granularity');
    }

    setAppliedDateRange(finalRange);
    setWorkingDateRange(finalRange);
    setPageIndex(0); // Reset to first page when date range changes
  }, [granularity]);

  const handleGranularityChange = React.useCallback((newGranularity: GranularityValue) => {
    setGranularity(newGranularity);

    // Adjust applied date range if needed when granularity changes
    if (appliedDateRange?.from && appliedDateRange?.to) {
      const days = differenceInDays(appliedDateRange.to, appliedDateRange.from) + 1;
      let adjusted = false;
      let newTo = appliedDateRange.to;

      if (newGranularity === 'daily' && days > 90) {
        newTo = addDays(appliedDateRange.from, 89);
        adjusted = true;
      } else if (newGranularity === 'hourly' && days > 14) {
        newTo = addDays(appliedDateRange.from, 13);
        adjusted = true;
      }

      if (adjusted) {
        const newRange = { from: appliedDateRange.from, to: newTo };
        setAppliedDateRange(newRange);
        setWorkingDateRange(newRange);
        toast.info(`Date range adjusted for ${newGranularity} granularity`);
      }
    }
  }, [appliedDateRange]);

  // Build API request for table
  const buildTableRequest = React.useCallback((): BreakdownTableRequest | null => {
    if (!appliedDateRange?.from || !appliedDateRange?.to) return null;

    const startDate = new Date(appliedDateRange.from);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(appliedDateRange.to);
    endDate.setHours(23, 59, 59, 999);

    // Determine sort parameters
    const sortEntry = sorting[0];
    const sortBy = sortEntry ? sortEntry.id : 'totalCosts';
    const sortOrder = sortEntry?.desc ? 'desc' : 'asc';

    // Convert metadata filters from SavedViewsContext format to API format
    const metadataFilters = currentFilters
      .filter((f) => f.tagName && f.operation && f.value)
      .map((f) => ({
        key: f.tagName,
        operator: (f.operation === 'is' ? 'eq' : 'neq') as 'eq' | 'neq',
        value: f.value,
      }));

    const request: BreakdownTableRequest = {
      date_from: startDate.toISOString(),
      date_to: endDate.toISOString(),
      group_by: groupBy.startsWith("metadata:") ? groupBy.replace("metadata:", "") : groupBy,
      granularity: granularity,
      sort_by: sortBy,
      sort_order: sortOrder as 'asc' | 'desc',
      skip: pageIndex * pageSize,
      limit: pageSize,
      model_selection: modelSelection.all || modelSelection.providers.length > 0 || modelSelection.models.length > 0
        ? modelSelection
        : null,
      providers: null,
      metadata: metadataFilters.length > 0 ? metadataFilters : null,
    };

    return request;
  }, [appliedDateRange, modelSelection, granularity, groupBy, pageIndex, pageSize, sorting, currentFilters]);

  // Build API request for timeseries
  const buildTimeSeriesRequest = React.useCallback((): BreakdownTimeSeriesRequest | null => {
    if (!appliedDateRange?.from || !appliedDateRange?.to) return null;

    const startDate = new Date(appliedDateRange.from);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(appliedDateRange.to);
    endDate.setHours(23, 59, 59, 999);

    // Convert metadata filters from SavedViewsContext format to API format
    const metadataFilters = currentFilters
      .filter((f) => f.tagName && f.operation && f.value)
      .map((f) => ({
        key: f.tagName,
        operator: (f.operation === 'is' ? 'eq' : 'neq') as 'eq' | 'neq',
        value: f.value,
      }));

    const request: BreakdownTimeSeriesRequest = {
      date_from: startDate.toISOString(),
      date_to: endDate.toISOString(),
      granularity: granularity,
      group_by: groupBy.startsWith("metadata:") ? groupBy.replace("metadata:", "") : groupBy,
      model_selection: modelSelection.all || modelSelection.providers.length > 0 || modelSelection.models.length > 0
        ? modelSelection
        : null,
      providers: null,
      metadata: metadataFilters.length > 0 ? metadataFilters : null,
    };

    return request;
  }, [appliedDateRange, modelSelection, granularity, groupBy, currentFilters]);

  const tableRequest = buildTableRequest();
  const timeSeriesRequest = buildTimeSeriesRequest();

  // Fetch data
  const { data: tableResponse, isLoading: isLoadingTable } = useBreakdownTable(tableRequest, tableRequest !== null);
  const { data: timeSeriesResponse, isLoading: isLoadingTimeSeries } = useBreakdownTimeSeries(timeSeriesRequest, timeSeriesRequest !== null);

  // Transform API response to table data, filtering out "Total" group
  const tableData: TableRowData[] = React.useMemo(() => {
    if (!tableResponse?.items) return [];
    return tableResponse.items
      .filter((item: BreakdownTableItem) => item.group !== 'Total')
      .map((item: BreakdownTableItem) => ({
        group: item.group,
        totalCosts: item.totalCosts,
        costPerAction: item.costPerAction,
        costDistribution: item.costDistribution,
        totalEvents: item.totalEvents,
        eventDistribution: item.eventDistribution,
        dayAverage: item.dayAverage,
        providers: item.providers,
      }));
  }, [tableResponse]);

  // Extract the "Total" group from API response
  const totalRow: TableRowData | null = React.useMemo(() => {
    if (!tableResponse?.items) return null;
    const totalItem = tableResponse.items.find((item: BreakdownTableItem) => item.group === 'Total');
    if (!totalItem) return null;
    return {
      group: totalItem.group,
      totalCosts: totalItem.totalCosts,
      costPerAction: totalItem.costPerAction,
      costDistribution: totalItem.costDistribution,
      totalEvents: totalItem.totalEvents,
      eventDistribution: totalItem.eventDistribution,
      dayAverage: totalItem.dayAverage,
      providers: totalItem.providers,
      isTotal: true,
    };
  }, [tableResponse]);

  const handleDownloadReport = async () => {
    if (!appliedDateRange?.from || !appliedDateRange?.to) {
      toast.error("Date range is required");
      return;
    }

    if (!user?.id) {
      toast.error("You must be logged in to download a report");
      return;
    }

    // Build metadata filters for API
    const metadataFilters: MetadataFilter[] | null = currentFilters.length > 0
      ? currentFilters
        .filter((f) => f.tagName && f.operation && f.value)
        .map((f) => ({
          key: f.tagName,
          operator: (f.operation === "is" ? "eq" : "neq") as "eq" | "neq",
          value: f.value,
        }))
      : null;

    // Determine group_by value - handle metadata: prefix
    const groupByValue = groupBy.startsWith("metadata:")
      ? groupBy.replace("metadata:", "")
      : groupBy;

    // Build payload
    const payload: ReportPayload = {
      report_type: "breakdown",
      group_by: groupByValue,
      model_selection: modelSelection.all || modelSelection.providers.length > 0 || modelSelection.models.length > 0
        ? modelSelection
        : null,
      metadata_filters: metadataFilters && metadataFilters.length > 0 ? metadataFilters : null,
    };

    const startDate = new Date(appliedDateRange.from);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(appliedDateRange.to);
    endDate.setHours(23, 59, 59, 999);

    // Generate report name with date range
    const reportName = `Cost Breakdown ${format(appliedDateRange.from, "MMM d")} - ${format(appliedDateRange.to, "MMM d, yyyy")}`;

    const request: CreateReportRequest = {
      name: reportName,
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      payload,
      recipient_user_ids: [user.id],
    };

    try {
      await createReportMutation.mutateAsync(request);
      toast.success("Report generation started. You will receive an email when it's ready.");
    } catch (error) {
      toast.error("Failed to create report");
      console.error("Failed to create report:", error);
    }
  };

  const dateRangeDays = React.useMemo(() => {
    if (!appliedDateRange?.from || !appliedDateRange?.to) return 0;
    return differenceInDays(appliedDateRange.to, appliedDateRange.from) + 1; // +1 to include both ends
  }, [appliedDateRange]);

  // Reset all filters to initial page load state
  const handleClearAllFilters = React.useCallback(() => {
    // Reset models to all models
    setModelSelection(ALL_MODELS_SELECTION);
    // Reset date range to default
    setWorkingDateRange(DEFAULT_DATE_RANGE);
    setAppliedDateRange(DEFAULT_DATE_RANGE);
    // Reset granularity and groupBy to defaults
    setGranularity('daily');
    setGroupBy('modality');
    // Reset pagination
    setPageIndex(0);
    // Clear metadata filters
    setCurrentFilters([]);
  }, [setCurrentFilters]);

  // Check if any filter has changed from initial state
  const hasFiltersChanged = React.useMemo(() => {
    // Check if date range changed from default (last 30 days)
    const defaultFrom = subDays(new Date(), 29);
    const defaultTo = new Date();
    const dateChanged = appliedDateRange?.from && appliedDateRange?.to && (
      format(appliedDateRange.from, 'yyyy-MM-dd') !== format(defaultFrom, 'yyyy-MM-dd') ||
      format(appliedDateRange.to, 'yyyy-MM-dd') !== format(defaultTo, 'yyyy-MM-dd')
    );
    // Check if models changed from default (all models)
    const modelsChanged = !modelSelection.all || modelSelection.providers.length > 0 || modelSelection.models.length > 0;
    // Check if granularity changed from default
    const granularityChanged = granularity !== 'daily';
    // Check if groupBy changed from default
    const groupByChanged = groupBy !== 'modality';
    // Check if any metadata filters exist (including incomplete ones being edited)
    const hasMetadataFilters = currentFilters.length > 0;
    return dateChanged || modelsChanged || granularityChanged || groupByChanged || hasMetadataFilters;
  }, [appliedDateRange, modelSelection, granularity, groupBy, currentFilters]);

  return (
    <div>
      <FilterContainer>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CustomDatePicker
              value={workingDateRange}
              onChange={setWorkingDateRange}
              onApply={handleDateRangeApply}
            />
            <ModelsSelector value={modelSelection} onChange={setModelSelection} />
            <GranularitySelector
              value={granularity}
              onChange={handleGranularityChange}
              dateRangeDays={dateRangeDays}
            />
            <GroupBySelector value={groupBy} onChange={setGroupBy} />
          </div>
          {hasFiltersChanged && (
            <div className="flex items-center gap-2 shrink-0">
              <div className="h-[30px] w-px bg-[lch(83.025_0_282.863)]" />
              <Button
                size="xs"
                variant="ghost"
                onClick={handleClearAllFilters}
              >
                Reset Filters
              </Button>
            </div>
          )}
        </div>
      </FilterContainer>
      <FilterContainer>
        <FiltersSelector onClearAll={handleClearAllFilters} hideResetButton />
      </FilterContainer>
      {isOnFreePlan && (
        <UpgradeBanner type="data-retention" />
      )}
      <DashboardCard
        icon={<BiDollarCircle />}
        title="Total Spend"
        subtitle="A breakdown of your AI costs over the selected date range. Use this to understand your overall budget consumption and identify spending trends over time."
        className="mx-4 lg:gap-2 lg:mx-6 my-4"
      >
        <div className="w-full min-h-[350px]">
          <LastXDaysSpend
            data={timeSeriesResponse?.data || []}
            groupBy={groupBy}
            granularity={granularity}
            dateRange={appliedDateRange}
            isLoading={isLoadingTimeSeries}
          />
        </div>
      </DashboardCard>

      <div className="mx-4 lg:mx-6 my-4 space-y-[13px]">
        <div className="flex items-center justify-between">
          <h2 className="text-[18px] font-medium text-[#08090A] px-[4px]">Table View</h2>
          <Button
            onClick={handleDownloadReport}
            disabled={createReportMutation.isPending}
            size="sm"
            className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium px-[13px] rounded-[6px]"
          >
            <IconDownload className="mr-[8px] size-4" />
            {createReportMutation.isPending ? "Generating..." : "Download Report"}
          </Button>
        </div>
        <div className="space-y-0">
          <DataTable
            columns={columns}
            data={tableData}
            className="w-full"
            loading={isLoadingTable}
            footerData={totalRow ? [totalRow] : []}
            enableSorting={true}
            sorting={sorting}
            onSortingChange={(newSorting) => {
              setSorting(newSorting);
              setPageIndex(0); // Reset to first page when sorting changes
            }}
            onRowClick={handleRowClick}
            pagination={{
              enabled: true,
              mode: 'server',
              pageIndex,
              pageSize,
              totalItems: tableResponse?.total || 0,
              pageSizeOptions: [5, 10, 20, 50],
              onPageChange: setPageIndex,
              onPageSizeChange: (newSize) => {
                setPageSize(newSize);
                setPageIndex(0);
              },
            }}
          />
        </div>
      </div>

      {/* Breakdown Detail Sidebar */}
      <BreakdownDetailSidebar
        open={isSidebarOpen}
        onOpenChange={handleSidebarClose}
        rowData={selectedRow}
        groupBy={groupBy.startsWith("metadata:") ? groupBy.replace("metadata:", "") : groupBy}
        dateRange={appliedDateRange}
        granularity={granularity}
        modelSelection={modelSelection}
        metadataFilters={
          currentFilters
            .filter((f) => f.tagName && f.operation && f.value)
            .map((f) => ({
              key: f.tagName,
              operator: (f.operation === 'is' ? 'eq' : 'neq') as 'eq' | 'neq',
              value: f.value,
            }))
        }
        timeSeriesData={timeSeriesResponse?.data || []}
      />
    </div>
  );
}